import { computed, inject, Injectable, signal } from '@angular/core';
import { ModulosService } from '../../features/modulos/services/modulos.service';
import { ModuleCategory, ModuleStatus } from '../../features/modulos/modulos.models';

/**
 * Chaves de capacidade consumidas pela navegação (e por qualquer gating de
 * feature no futuro). São mais granulares do que as categorias amplas que o
 * backend expõe hoje em `/api/tenant-modules`.
 *
 * TODO: aguardando backend
 * Frontend issue: GOWOTecnologia/gowo.hub.front (rollout menu dinâmico)
 * Backend issue:  GOWOTecnologia/kram.hub.api (expor capacidades granulares)
 * Enquanto o backend só retornar `category`, o mapa abaixo deriva as
 * capacidades a partir da categoria contratada. Capacidade que nenhuma
 * categoria consegue mapear fica "fail-open" (ver `hasCapability`); por isso
 * toda chave usada em `requiredModules` precisa estar em `MAPPABLE_CAPABILITIES`
 * (travado por teste em `navigation.service.spec.ts`).
 */
export type Capability =
  | 'marketplace.dashboard'
  | 'marketplace.questions'
  | 'marketplace.orders'
  | 'marketplace.products'
  | 'logistics.integration'
  | 'logistics.separation'
  | 'logistics.separationDashboard'
  | 'logistics.storage'
  | 'logistics.storageDashboard'
  | 'erp.integration'
  | 'financial.integration'
  | 'crm.integration'
  | 'cylinders.integration'
  | 'cylinders.loads'
  // BI & Analytics (#946): vem da categoria `bi`, não de `monitoramento` (que é de Alertas).
  | 'bi.integration'
  // Capacidades transversais derivadas de categorias
  | 'products'
  | 'orders'
  // Notas fiscais: nascem no faturamento do ERP e as ocorrências do Frete caem na mesma tela.
  | 'invoices'
  // Especial: verdadeira quando o tenant tem qualquer módulo contratado.
  | 'anyIntegration';

/** Status que representam módulo efetivamente contratado (≠ apenas disponível). */
const CONTRACTED_STATUSES = new Set<ModuleStatus>([
  'contratado', 'ativo', 'pausado', 'teste',
]);

/**
 * Mapa único categoria → capacidades desbloqueadas.
 * ÚNICA fonte do mapeamento no frontend — não replicar em componentes.
 */
// Exportado só para o invariante de `modulos.service.spec.ts` (categoria com capacidade precisa
// sobreviver ao `toCategory`).
export const MODULE_CAPABILITIES: Partial<Record<ModuleCategory, Capability[]>> = {
  marketplaces: [
    'marketplace.dashboard', 'marketplace.questions',
    'marketplace.orders', 'marketplace.products',
    'products', 'orders',
  ],
  logistica: [
    'logistics.integration', 'logistics.separation', 'logistics.separationDashboard',
    'logistics.storage', 'logistics.storageDashboard',
    'invoices',
  ],
  erps: ['erp.integration', 'products', 'orders', 'invoices'],
  financeiro: ['financial.integration'],
  crm: ['crm.integration'],
  cilindros: ['cylinders.integration', 'cylinders.loads'],
  bi: ['bi.integration'],
};

/** Conjunto de todas as capacidades que ALGUMA categoria consegue mapear. */
const MAPPABLE_CAPABILITIES = new Set<Capability>(
  Object.values(MODULE_CAPABILITIES).flat() as Capability[],
);

/**
 * True quando a chave de `requiredModules` é avaliada de verdade: `anyIntegration`
 * (especial) ou capacidade que alguma categoria desbloqueia. Chave que falha aqui
 * aparece para todo tenant (fail-open) sem ninguém perceber.
 */
export function isMappableCapability(cap: string): boolean {
  return cap === 'anyIntegration' || MAPPABLE_CAPABILITIES.has(cap as Capability);
}

/**
 * Fonte única (signal) dos módulos contratados pelo tenant ativo.
 *
 * Reaproveita `ModulosService` (`GET /api/tenant-modules`) — não cria segunda
 * fonte de verdade. Carregado por `load()` (chamado pelo AppShell ao entrar no
 * tenant, inclusive após F5).
 *
 * Estratégia de rollout: enquanto os módulos não carregarem, ou o endpoint
 * falhar, `hasCapability` retorna `true` (fail-open) para não esconder itens de
 * tenants já existentes. O gating estrito passa a valer assim que os módulos
 * forem carregados com sucesso e o backend expuser as capacidades.
 */
@Injectable({ providedIn: 'root' })
export class ModuleAccessService {
  private readonly modulos = inject(ModulosService);

  private readonly _loaded = signal(false);
  private readonly _contractedCategories = signal<Set<ModuleCategory>>(new Set());
  /** Tenant cujos módulos estão carregados — dispara reload ao trocar de tenant. */
  private loadedTenantId: string | null = null;

  /** True depois que os módulos do tenant foram carregados com sucesso. */
  readonly loaded = this._loaded.asReadonly();

  /** Categorias efetivamente contratadas pelo tenant ativo. */
  readonly contractedCategories = this._contractedCategories.asReadonly();

  /** Capacidades ativas derivadas das categorias contratadas. */
  readonly activeCapabilities = computed<Set<Capability>>(() => {
    const caps = new Set<Capability>();
    for (const category of this._contractedCategories()) {
      for (const cap of MODULE_CAPABILITIES[category] ?? []) {
        caps.add(cap);
      }
    }
    return caps;
  });

  /** True quando o tenant possui ao menos um módulo contratado. */
  readonly hasAnyModule = computed(() => this._contractedCategories().size > 0);

  /**
   * Carrega os módulos contratados de um tenant. Idempotente para o mesmo
   * tenant; recarrega automaticamente quando o `tenantId` muda.
   */
  load(tenantId: string): void {
    if (this._loaded() && this.loadedTenantId === tenantId) return;
    this.loadedTenantId = tenantId;
    this.modulos.getModules().subscribe({
      next: modules => {
        // Ignora resposta obsoleta se o tenant já mudou durante o request.
        if (this.loadedTenantId !== tenantId) return;
        const categories = new Set<ModuleCategory>();
        for (const m of modules) {
          if (CONTRACTED_STATUSES.has(m.status)) categories.add(m.category);
        }
        this._contractedCategories.set(categories);
        this._loaded.set(true);
      },
      // Falha do endpoint mantém fail-open (loaded=false) — não esconde o menu.
      error: () => {
        if (this.loadedTenantId === tenantId) this._loaded.set(false);
      },
    });
  }

  /** Limpa o estado (troca de tenant / logout). */
  clear(): void {
    this.loadedTenantId = null;
    this._contractedCategories.set(new Set());
    this._loaded.set(false);
  }

  /**
   * Verifica se uma capacidade está disponível para o tenant ativo.
   *
   * Fail-open em dois casos (rollout / lacuna de backend):
   * 1. Módulos ainda não carregados (`loaded() === false`).
   * 2. Capacidade que nenhuma categoria consegue mapear hoje — rede de segurança;
   *    o menu não deve depender dela (ver `isMappableCapability`).
   */
  hasCapability(cap: Capability): boolean {
    if (cap === 'anyIntegration') {
      return !this._loaded() || this.hasAnyModule();
    }
    if (!this._loaded()) return true;
    if (!MAPPABLE_CAPABILITIES.has(cap)) return true;
    return this.activeCapabilities().has(cap);
  }

  /**
   * Variante fail-closed: só é verdadeira com os módulos carregados e a capacidade ativa.
   * Para áreas que não podem aparecer nem por um instante a quem não contratou (BI, #946).
   */
  hasCapabilityStrict(cap: Capability): boolean {
    return this._loaded() && this.activeCapabilities().has(cap);
  }

  /** Avalia uma lista de capacidades com o modo (`any`/`all`). */
  matches(caps: string[] | undefined, mode: 'any' | 'all' = 'any'): boolean {
    if (!caps || caps.length === 0) return true;
    const check = (c: string) => this.hasCapability(c as Capability);
    return mode === 'all' ? caps.every(check) : caps.some(check);
  }
}
